/**
 * Convert a webcam screenshot to base64 data URL with 2:3 aspect ratio enforcement
 * @param {Object} webcamRef - React ref to webcam component
 * @returns {Promise<string|null>} Base64 data URL or null if failed
 */
export const captureWebcamImage = async (webcamRef) => {
  if (!webcamRef.current) {
    console.error('Webcam ref not available');
    return null;
  }

  try {
    const imageSrc = webcamRef.current.getScreenshot();
    // Ensure 2:3 aspect ratio for consistent overlay placement
    const croppedImage = await cropToAspectRatio(imageSrc, 2, 3);
    return croppedImage;
  } catch (error) {
    console.error('Error capturing webcam image:', error);
    return null;
  }
};

/**
 * Crop image to specific aspect ratio (2:3 for overlay consistency)
 * @param {string} base64Data - Base64 data URL
 * @param {number} targetWidth - Target aspect ratio width (e.g., 2)
 * @param {number} targetHeight - Target aspect ratio height (e.g., 3)
 * @returns {Promise<string>} Cropped base64 data URL
 */
export const cropToAspectRatio = (base64Data, targetWidth = 2, targetHeight = 3) => {
  return new Promise((resolve, reject) => {
    const img = new Image();

    img.onload = () => {
      const canvas = document.createElement('canvas');
      const targetRatio = targetWidth / targetHeight;
      const currentRatio = img.width / img.height;

      let sourceWidth = img.width;
      let sourceHeight = img.height;
      let sourceX = 0;
      let sourceY = 0;

      // Crop to target aspect ratio (center crop)
      if (currentRatio > targetRatio) {
        // Image is wider than target - crop sides
        sourceWidth = img.height * targetRatio;
        sourceX = (img.width - sourceWidth) / 2;
      } else if (currentRatio < targetRatio) {
        // Image is taller than target - crop top/bottom
        sourceHeight = img.width / targetRatio;
        sourceY = (img.height - sourceHeight) / 2;
      }

      // Set canvas to maintain high quality while preserving 2:3
      const outputWidth = 1280; // 2:3 ratio
      const outputHeight = 1920;

      canvas.width = outputWidth;
      canvas.height = outputHeight;

      const ctx = canvas.getContext('2d', { alpha: false });

      // Enable high-quality rendering
      ctx.imageSmoothingEnabled = true;
      ctx.imageSmoothingQuality = 'high';

      ctx.drawImage(
        img,
        sourceX, sourceY, sourceWidth, sourceHeight,
        0, 0, outputWidth, outputHeight
      );

      // Use high quality for best results
      const croppedBase64 = canvas.toDataURL('image/jpeg', 0.98);
      console.log('[CROP] Output size:', (croppedBase64.length / 1024).toFixed(0), 'KB');
      resolve(croppedBase64);
    };

    img.onerror = () => {
      reject(new Error('Failed to load image for cropping'));
    };

    img.src = base64Data;
  });
};

/**
 * Convert base64 data URL to Blob
 * @param {string} base64Data - Base64 data URL
 * @returns {Blob} Blob object
 */
export const base64ToBlob = (base64Data) => {
  const parts = base64Data.split(';base64,');
  const contentType = parts[0].split(':')[1];
  const raw = window.atob(parts[1]);
  const rawLength = raw.length;
  const uInt8Array = new Uint8Array(rawLength);

  for (let i = 0; i < rawLength; ++i) {
    uInt8Array[i] = raw.charCodeAt(i);
  }

  return new Blob([uInt8Array], { type: contentType });
};

/**
 * Download image to device
 * @param {string} base64Data - Base64 data URL
 * @param {string} filename - Filename for download
 */
export const downloadImage = (base64Data, filename = 'photo-booth-memory.jpg') => {
  try {
    const link = document.createElement('a');
    link.href = base64Data;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  } catch (error) {
    console.error('Error downloading image:', error);
  }
};

/**
 * Validate image size and format
 * @param {string} base64Data - Base64 data URL
 * @returns {Object} Validation result with isValid and error message
 */
export const validateImage = (base64Data) => {
  const MAX_SIZE_MB = 10;

  try {
    // Check if it's a valid base64 data URL
    if (!base64Data || !base64Data.startsWith('data:image/')) {
      return {
        isValid: false,
        error: 'Invalid image format'
      };
    }

    // Calculate size in MB
    const sizeInBytes = (base64Data.length * 3) / 4;
    const sizeInMB = sizeInBytes / (1024 * 1024);

    if (sizeInMB > MAX_SIZE_MB) {
      return {
        isValid: false,
        error: `Image size (${sizeInMB.toFixed(2)}MB) exceeds maximum allowed size (${MAX_SIZE_MB}MB)`
      };
    }

    return {
      isValid: true,
      sizeInMB: sizeInMB.toFixed(2)
    };
  } catch (error) {
    return {
      isValid: false,
      error: 'Error validating image'
    };
  }
};

/**
 * Resize image if needed (for optimization)
 * @param {string} base64Data - Base64 data URL
 * @param {number} maxWidth - Maximum width in pixels
 * @param {number} maxHeight - Maximum height in pixels
 * @returns {Promise<string>} Resized base64 data URL
 */
export const resizeImage = (base64Data, maxWidth = 1920, maxHeight = 1080) => {
  return new Promise((resolve, reject) => {
    const img = new Image();

    img.onload = () => {
      const canvas = document.createElement('canvas');
      let width = img.width;
      let height = img.height;

      // Calculate new dimensions while maintaining aspect ratio
      if (width > maxWidth || height > maxHeight) {
        const aspectRatio = width / height;

        if (width > height) {
          width = maxWidth;
          height = width / aspectRatio;
        } else {
          height = maxHeight;
          width = height * aspectRatio;
        }
      }

      canvas.width = width;
      canvas.height = height;

      const ctx = canvas.getContext('2d', { alpha: false });

      // Enable high-quality rendering
      ctx.imageSmoothingEnabled = true;
      ctx.imageSmoothingQuality = 'high';

      ctx.drawImage(img, 0, 0, width, height);

      const resizedBase64 = canvas.toDataURL('image/jpeg', 0.98);
      resolve(resizedBase64);
    };

    img.onerror = () => {
      reject(new Error('Failed to load image for resizing'));
    };

    img.src = base64Data;
  });
};

/**
 * Parse image ID from URL query parameters
 * @returns {string|null} Image ID or null if not present
 */
export const getImageIdFromUrl = () => {
  const params = new URLSearchParams(window.location.search);
  return params.get('imageId');
};

/**
 * Upscale image to higher resolution for better quality
 * @param {string} base64Data - Base64 data URL
 * @param {number} targetWidth - Target width in pixels
 * @param {number} targetHeight - Target height in pixels
 * @returns {Promise<string>} Upscaled base64 data URL
 */
export const upscaleImage = (base64Data, targetWidth = 2560, targetHeight = 3840) => {
  return new Promise((resolve, reject) => {
    const img = new Image();

    img.onload = () => {
      const canvas = document.createElement('canvas');
      canvas.width = targetWidth;
      canvas.height = targetHeight;

      const ctx = canvas.getContext('2d', { alpha: false });

      // Enable maximum quality image smoothing for upscaling
      ctx.imageSmoothingEnabled = true;
      ctx.imageSmoothingQuality = 'high';

      // Draw image at higher resolution
      ctx.drawImage(img, 0, 0, targetWidth, targetHeight);

      // Use JPEG with high quality (98%) to keep resolution while reducing file size
      const upscaledBase64 = canvas.toDataURL('image/jpeg', 0.98);
      resolve(upscaledBase64);
    };

    img.onerror = () => {
      reject(new Error('Failed to load image for upscaling'));
    };

    img.src = base64Data;
  });
};

/**
 * Compress image for efficient upload while maintaining good quality
 * @param {string} base64Data - Base64 image data
 * @param {number} maxWidth - Maximum width (default 1920 for HD quality)
 * @param {number} quality - JPEG quality 0-1 (default 0.85)
 * @returns {Promise<string>} Compressed base64 image
 */
export const compressForUpload = (base64Data, maxWidth = 1920, quality = 0.85) => {
  return new Promise((resolve, reject) => {
    const img = new Image();

    img.onload = () => {
      const canvas = document.createElement('canvas');

      // Calculate dimensions maintaining aspect ratio
      const aspectRatio = img.width / img.height;
      let width = maxWidth;
      let height = width / aspectRatio;

      // Don't upscale if image is smaller
      if (img.width < maxWidth) {
        width = img.width;
        height = img.height;
      }

      canvas.width = width;
      canvas.height = height;

      const ctx = canvas.getContext('2d', { alpha: false });
      ctx.imageSmoothingEnabled = true;
      ctx.imageSmoothingQuality = 'high';

      ctx.drawImage(img, 0, 0, width, height);

      // Lower quality for upload - still looks great but much smaller file
      const compressed = canvas.toDataURL('image/jpeg', quality);

      console.log('[COMPRESS] Original:', (base64Data.length / 1024 / 1024).toFixed(2), 'MB');
      console.log('[COMPRESS] Compressed:', (compressed.length / 1024 / 1024).toFixed(2), 'MB');
      console.log('[COMPRESS] Reduction:', (100 - (compressed.length / base64Data.length * 100)).toFixed(1), '%');

      resolve(compressed);
    };

    img.onerror = () => reject(new Error('Compression failed'));
    img.src = base64Data;
  });
};
